import { PropsWithChildren } from "react";
import React from "react";

import { DialogClose } from "@radix-ui/react-dialog";
import { ChevronDown } from "lucide-react";

import { LoadingButton } from "@/components/common/loading-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export type MergeButtonProps = {
  resource: "genres" | "artists" | "venues";
  loading: boolean;
  onConfirm: (close: () => void) => void;
  /**
   * Overrides the dialog body. The default warns that the merge is permanent,
   * which is true for genres and venues. An artist merge is reversible (there is
   * an Unmerge action), so telling a curator otherwise would scare them off a
   * safe operation.
   */
  description?: React.ReactNode;
};

export const MergeButton = ({
  resource,
  children,
  loading = false,
  onConfirm,
  description,
}: PropsWithChildren<MergeButtonProps>) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
      <DialogTrigger asChild>
        <Button type="button" variant="destructive">
          Merge
          <ChevronDown />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge {resource}?</DialogTitle>
          <DialogDescription>
            {description ?? (
              <>
                Are you sure you want to merge these {resource}? This action
                cannot be undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <LoadingButton
            isLoading={loading}
            disabled={loading}
            variant="destructive"
            onClick={() => onConfirm(() => setOpen(false))}
          >
            Merge
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
